/* Collection-only enhancement. Shared theme pagination and assets remain unchanged. */
(() => {
  if (customElements.get('tsn-collection-pagination')) return;
  const storageKey = 'tsn:collection-return:v1';
  const cacheKey = 'tsn:collection-batches:v2';
  let initialRestoreChecked = false;
  const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));
  function routeKey(theme) {
    const url = new URL(location.href);
    url.searchParams.delete('preview_theme_id');
    url.searchParams.delete('_ab');
    url.searchParams.delete('_fd');
    url.searchParams.delete('_sc');
    url.searchParams.sort();
    return `${theme}:${url.pathname}?${url.searchParams}`;
  }
  class CollectionPagination extends HTMLElement {
    connectedCallback() {
      // Custom elements may be upgraded before their children finish parsing.
      queueMicrotask(() => this.start());
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.viewObserver?.disconnect();
      this.controller?.abort();
      this.events?.abort();
    }
    async start() {
      if (!this.isConnected || this.events) return;
      this.root = this.closest('.shopify-section');
      this.grid = this.root?.querySelector('#items-grid');
      if (!this.grid) return;
      this.events = new AbortController();
      this.urls = this._tsnReturn?.urls || [];
      this.batches = this._tsnReturn?.batches || [];
      this.key = routeKey(this.dataset.theme);
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-tsn-more]');
        if (!link || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.load(link.href, true);
      }, { signal: this.events.signal });
      this.root.addEventListener('click', event => {
        const link = event.target.closest('a[href]');
        if (link && this.grid.contains(link) && new URL(link.href).pathname.includes('/products/')) {
          this.save(link);
          this.savedClickAt = Date.now();
        }
      }, { capture: true, signal: this.events.signal });
      window.addEventListener('pagehide', () => {
        // Preserve the exact pre-navigation card; teardown may already move the header.
        if (!this.savedClickAt || Date.now() - this.savedClickAt > 5000) this.save();
      }, { signal: this.events.signal });
      window.addEventListener('pageshow', event => {
        if (event.persisted) { this.busy = false; this.setBusy(false); }
      }, { signal: this.events.signal });
      if (!initialRestoreChecked) {
        initialRestoreChecked = true;
        await this.restore();
      }
      // One automatic batch only; keeps the footer reachable and a visible fallback.
      if (this.dataset.mode === 'infinite_scroll' && !this.urls.length &&
          !navigator.connection?.saveData && !/2g/.test(navigator.connection?.effectiveType || '') &&
          'IntersectionObserver' in window && !window.Shopify?.designMode) {
        this.observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          const link = this.querySelector('[data-tsn-more]');
          this.observer.disconnect();
          if (link) this.load(link.href, false);
        }, { rootMargin: '400px 0px' });
        this.observer.observe(this);
      }
    }
    track(action) {
      // Separate live collection measurement from draft QA; reuse the consent-aware tracker.
      if (window.Shopify?.designMode || this.restoring) return;
      const live = this.dataset.theme === '189130866861' && window.Shopify?.theme?.role === 'main';
      const draft = this.dataset.theme === '189166649517' && window.Shopify?.theme?.role !== 'main';
      if (!live && !draft) return;
      try {
        const privacy = window.Shopify?.customerPrivacy;
        if (typeof privacy?.analyticsProcessingAllowed === 'function' && !privacy.analyticsProcessingAllowed()) { this.dataset.tsnAnalytics = 'consent-denied'; return; }
        if (typeof window.clarity !== 'function') { this.dataset.tsnAnalytics = 'tracker-unavailable'; return; }
        window.clarity('set', 'tsn_collection_release', live ? 'live_2026_10_12' : 'draft_189166649517');
        window.clarity('event', `tsn_${live ? 'live' : 'draft'}_collection_${action}`);
        this.dataset.tsnAnalytics = `dispatched:${action}`;
      } catch (_) { /* Analytics must never interrupt shopping. */ }
    }
    watchAutoBatch(items) {
      if (!('IntersectionObserver' in window)) return;
      const firstProduct = items.find(item => item.querySelector('a[href*="/products/"]') && !item.classList.contains('promotion-block'));
      if (!firstProduct) return;
      this.viewObserver?.disconnect();
      this.viewObserver = new IntersectionObserver(entries => {
        if (!this.isConnected || this.key !== routeKey(this.dataset.theme)) { this.viewObserver.disconnect(); return; }
        if (!entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
        this.viewObserver.disconnect();
        this.track('auto_batch_viewed');
      }, { threshold: 0.5 });
      this.viewObserver.observe(firstProduct);
    }
    setBusy(busy) {
      this.setAttribute('aria-busy', String(busy));
      const link = this.querySelector('[data-tsn-more]');
      if (link) {
        link.setAttribute('aria-disabled', String(busy));
        link.textContent = busy ? 'Loading products…' : 'Show more products';
      }
    }
    async load(href, focusNew = false) {
      if (this.busy || !this.isConnected) return false;
      const url = new URL(href, location.origin);
      if (url.origin !== location.origin || url.pathname !== location.pathname) return false;
      const route = this.key;
      const loadType = this.restoring ? 'restore' : (focusNew ? 'manual' : 'auto');
      if (loadType === 'manual') this.track('show_more_click');
      this.busy = true;
      this.observer?.disconnect();
      this.setBusy(true);
      const status = this.querySelector('[data-tsn-status]');
      const fallback = this.querySelector('[data-tsn-fallback]');
      status.textContent = 'Loading products…';
      fallback.hidden = true;
      const pageURL = url.pathname + url.search;
      url.searchParams.set('section_id', this.dataset.section);
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), 15000);
      try {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Collection request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.querySelector('tsn-collection-pagination');
        const items = [...html.querySelectorAll('#items-grid > .grid-item')];
        if (!source || !items.length) throw new Error('Collection response incomplete');
        if (!this.isConnected || route !== routeKey(this.dataset.theme)) return false;
        // Capture server markup before Alpine, sliders and reviews mutate it.
        const batch = {
          html: items.map(item => item.outerHTML).join(''),
          shown: source.dataset.shown,
          count: source.querySelector('[data-tsn-count]').textContent,
          next: source.querySelector('[data-tsn-more]')?.getAttribute('href') || null,
          at: Date.now()
        };
        const fragment = document.createDocumentFragment();
        items.forEach(item => fragment.append(item));
        this.grid.append(fragment);
        this.urls.push(pageURL);
        this.batches.push(batch);
        this.cacheBatches();
        const added = Number(source.dataset.shown) - Number(this.dataset.shown);
        this.dataset.shown = source.dataset.shown;
        this.querySelector('[data-tsn-count]').textContent = source.querySelector('[data-tsn-count]').textContent;
        const next = source.querySelector('[data-tsn-more]');
        const link = this.querySelector('[data-tsn-more]');
        if (next) link.href = next.getAttribute('href');
        else link?.remove();
        status.textContent = next ? `${added} more products loaded.` : 'You’ve seen all products in this collection.';
        document.dispatchEvent(new CustomEvent('eurus:collection:reloaded'));
        if (loadType !== 'restore') this.track(`${loadType}_batch_loaded`);
        if (loadType === 'auto') this.watchAutoBatch(items);
        if (focusNew) {
          const firstLink = items[0].querySelector('a[href*="/products/"]');
          firstLink?.focus({ preventScroll: true });
          window.scrollTo({ top: scrollY + items[0].getBoundingClientRect().top - 180, behavior: 'instant' });
        }
        return true;
      } catch (error) {
        if (this.isConnected) {
          if (route === routeKey(this.dataset.theme) && loadType !== 'restore') this.track(`${loadType}_load_failed`);
          status.textContent = 'Products couldn’t load. Please try again or open the next page.';
          fallback.href = pageURL;
          fallback.hidden = false;
        }
        return false;
      } finally {
        clearTimeout(timeout);
        this.busy = false;
        this.setBusy(false);
      }
    }
    cacheBatches() {
      try {
        // One collection per tab; never grow an unbounded browsing cache.
        sessionStorage.removeItem(cacheKey);
        const value = JSON.stringify({ key: this.key, section: this.dataset.section, urls: this.urls, batches: this.batches });
        if (value.length < 2200000) sessionStorage.setItem(cacheKey, value);
      } catch (_) { /* Quota/private mode falls back to the existing network restore. */ }
    }
    save(clickedLink) {
      if (this.restoring || !this.isConnected || this.key !== routeKey(this.dataset.theme)) return;
      const links = [...this.grid.querySelectorAll('a[href*="/products/"]')];
      const anchor = clickedLink || links.find(link => {
        const rect = link.getBoundingClientRect();
        return rect.height > 0 && rect.bottom > 150;
      });
      const item = anchor?.closest('.grid-item');
      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          key: this.key, urls: this.urls, y: scrollY,
          itemIndex: item ? [...this.grid.children].indexOf(item) : -1, itemOffset: item?.getBoundingClientRect().top,
          anchor: anchor?.getAttribute('href'), offset: anchor?.getBoundingClientRect().top,
          at: Date.now()
        }));
      } catch (_) { /* Storage restrictions must not break browsing. */ }
    }
    async restore() {
      if (this._tsnReturn) { delete this._tsnReturn; return; }
      if (performance.getEntriesByType('navigation')[0]?.type !== 'back_forward') return;
      let state;
      try { state = JSON.parse(sessionStorage.getItem(storageKey)); } catch (_) { return; }
      if (!state || state.key !== this.key || Date.now() - state.at > 1800000 || !Array.isArray(state.urls)) return;
      this.restoring = true;
      let complete = true;
      for (const url of state.urls) {
        if (!await this.load(url)) { complete = false; break; }
      }
      await nextFrame();
      await nextFrame();
      if (this.isConnected && complete) {
        const anchor = [...this.grid.querySelectorAll('a[href]')].find(link => link.getAttribute('href') === state.anchor);
        const y = anchor && Number.isFinite(state.offset) ? scrollY + anchor.getBoundingClientRect().top - state.offset : state.y;
        window.scrollTo({ top: y, behavior: 'instant' });
      }
      this.restoring = false;
    }
  }
  customElements.define('tsn-collection-pagination', CollectionPagination);
})();
